import { useState } from "react";
import { motion } from "framer-motion";
import { Layers, Loader2, Sparkles, TrendingUp, TrendingDown, Trophy, Plus, Minus } from "lucide-react";
import { GameLabel } from "./TeamLogo";
import { toast } from "sonner";
import { api, fmtOdds } from "../lib/api";
import { useSlip } from "../context/SlipContext";
import { ConfidenceRing } from "./ConfidenceRing";

const MIN_LEGS = 2;
const MAX_LEGS = 15;

export const ConfidenceParlay = ({ sport, source = "demo" }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [note, setNote] = useState(null);
  const [nLegs, setNLegs] = useState(3);
  const { addParlay } = useSlip();

  const run = async (refresh = false) => {
    setLoading(true);
    setNote(null);
    if (!refresh) setData(null);
    try {
      const res = await api.get("/ai/confidence-parlay", {
        params: { sport, source, legs: nLegs, ...(refresh ? { refresh: true } : {}) },
      });
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not build a parlay.";
      if (e.response?.status === 404) setNote(msg);
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#233248] bg-gradient-to-b from-[#16233a] to-[#141C2B] p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#00E5FF]/15 grid place-items-center">
            <Layers className="h-4 w-4 text-[#00E5FF]" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-white text-base leading-tight">Confidence Parlay</h3>
            <p className="text-[11px] text-slate-400">AI builds today's highest-confidence {sport} parlay</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full">
          <div className="rounded-xl border border-[#233248] bg-gradient-to-br from-[#141C2B] to-[#101826] p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-semibold text-slate-200">How many legs</span>
                <p className="text-[11px] text-slate-500 font-data">AI picks the strongest</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="parlay-legs-dec"
                  onClick={() => setNLegs(Math.max(MIN_LEGS, nLegs - 1))}
                  disabled={nLegs <= MIN_LEGS}
                  className="h-8 w-8 grid place-items-center rounded-lg bg-[#0B0F17] border border-[#233248] text-white hover:border-[#00E5FF]/50 disabled:opacity-40 active:scale-95 transition-transform"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid="parlay-legs-count" className="font-data font-extrabold text-2xl w-8 text-center text-[#00E5FF]">{nLegs}</span>
                <button
                  data-testid="parlay-legs-inc"
                  onClick={() => setNLegs(Math.min(MAX_LEGS, nLegs + 1))}
                  disabled={nLegs >= MAX_LEGS}
                  className="h-8 w-8 grid place-items-center rounded-lg bg-[#0B0F17] border border-[#233248] text-white hover:border-[#00E5FF]/50 disabled:opacity-40 active:scale-95 transition-transform"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <input
              data-testid="parlay-legs-slider"
              type="range"
              min={MIN_LEGS}
              max={MAX_LEGS}
              value={nLegs}
              onChange={(e) => setNLegs(Number(e.target.value))}
              className="w-full accent-[#00E5FF] cursor-pointer"
            />
            <div className="flex justify-between text-[11px] text-slate-500 font-data mt-1">
              <span>{MIN_LEGS}–{MAX_LEGS} legs</span>
              <span>max {MAX_LEGS}</span>
            </div>
          </div>
          <button
            data-testid="build-parlay-button"
            onClick={() => run(!!data)}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#00E5FF] text-[#0B0F17] text-sm font-semibold px-3 py-2 hover:bg-[#33EaFF] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Building…" : data ? "Rebuild" : "Build Parlay"}
          </button>
        </div>
      </div>

      {note && !loading && (
        <p className="text-sm text-slate-400 text-center py-6" data-testid="parlay-note">
          {note}
        </p>
      )}
      {!data && !loading && !note && (
        <p className="text-sm text-slate-500 text-center py-6">
          Pick your leg count and tap <span className="text-[#00E5FF]">Build Parlay</span> — the AI assembles the sharpest combo of the day.
        </p>
      )}

      {data && !loading && (
        <div>
          <div className="rounded-xl border border-[#00E5FF]/30 bg-[#0B0F17]/50 p-3 mb-3">
            <div className="flex items-center gap-3">
              <ConfidenceRing value={data.parlay_confidence || 0} size={54} />
              <div className="grid grid-cols-3 gap-2 flex-1 min-w-0">
                <div>
                  <div className="font-data font-bold text-[#00E5FF] text-base">{fmtOdds(data.combined_odds)}</div>
                  <div className="text-[10px] text-slate-500">parlay odds</div>
                </div>
                <div>
                  <div className="font-data font-bold text-white text-base">${data.payout10}</div>
                  <div className="text-[10px] text-slate-500">$10 pays</div>
                </div>
                <div>
                  <div className="font-data font-bold text-white text-base">{data.legs.length}</div>
                  <div className="text-[10px] text-slate-500">legs</div>
                </div>
              </div>
            </div>
            <button
              data-testid="add-parlay-to-slip-button"
              onClick={() => addParlay(data.legs)}
              className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#00E5FF] text-[#0B0F17] text-xs font-semibold px-3 py-2 hover:bg-[#33EaFF]"
            >
              <Plus className="h-3.5 w-3.5" /> Add all legs to slip
            </button>
          </div>

          <div className="space-y-2" data-testid="parlay-legs-list">
            {data.legs.map((l, i) => {
              const winner = l.type === "winner";
              const over = (l.selection || "").toUpperCase() === "OVER";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3 rounded-xl border border-[#233248] bg-[#0B0F17]/50 p-3"
                >
                  <div className="h-7 w-7 rounded-md grid place-items-center shrink-0 bg-[#1B273E]">
                    {winner ? (
                      <Trophy className="h-3.5 w-3.5 text-[#F59E0B]" />
                    ) : over ? (
                      <TrendingUp className="h-3.5 w-3.5 text-[#10B981]" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5 text-[#EF4444]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold text-sm truncate">
                      {winner ? `${l.team} to win` : l.player_name}
                    </div>
                    <div className="font-data text-[11px] text-slate-400 flex items-center gap-1 flex-wrap">
                      {winner ? "moneyline" : `${l.selection} ${l.line} ${l.metric}`}
                      {l.game_label && <>· <GameLabel sport={sport} label={l.game_label} /></>}
                    </div>
                    {l.reasoning && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{l.reasoning}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-data text-sm text-white">{fmtOdds(l.odds)}</div>
                    {l.confidence != null && (
                      <div className="font-data text-[11px] text-[#00E5FF]">{l.confidence}%</div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {data.rationale && <p className="text-sm text-slate-300 mt-3 leading-relaxed">{data.rationale}</p>}
          {data.correlation_note && (
            <p className="text-xs text-slate-400 mt-2 italic">{data.correlation_note}</p>
          )}
        </div>
      )}
    </div>
  );
};
